import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getManagerDashboard } from '../services/apiManagerDashboard';
import AssignmentRulesPanel from '../components/manager/AssignmentRulesPanel';

const STAGE_COLORS = {
    new: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
    contacted: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
    qualified: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
    proposal: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
    negotiation: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
    won: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
    lost: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
};

function KpiCard({ icon, label, value, sub, color = 'text-primary' }) {
    return (
        <div className="bg-white dark:bg-surface-dark rounded-xl p-5 border border-gray-100 dark:border-gray-800 flex items-start gap-4">
            <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <span className={`material-symbols-outlined ${color}`}>{icon}</span>
            </div>
            <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-text-secondary mb-1">{label}</p>
                <p className="text-2xl font-bold">{value}</p>
                {sub && <p className="text-xs text-text-secondary mt-0.5">{sub}</p>}
            </div>
        </div>
    );
}

function AgentRow({ agent }) {
    const initials = agent.name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();
    const loadPct = Math.min(100, Math.round((agent.active_contacts / (agent.max_leads || 50)) * 100));
    const loadColor = loadPct >= 80 ? 'bg-red-500' : loadPct >= 50 ? 'bg-amber-500' : 'bg-emerald-500';

    return (
        <tr className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors">
            {/* Agent */}
            <td className="py-4 pl-6 pr-3">
                <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary/20 text-primary font-bold text-sm flex items-center justify-center shrink-0">
                        {initials}
                    </div>
                    <div>
                        <p className="font-semibold text-sm">{agent.name}</p>
                        <p className="text-xs text-text-secondary">{agent.email}</p>
                    </div>
                </div>
            </td>

            {/* Carga de Cartera */}
            <td className="py-4 px-3">
                <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-full max-w-[80px]">
                        <div className={`h-2 rounded-full transition-all ${loadColor}`} style={{ width: `${loadPct}%` }} />
                    </div>
                    <span className="text-sm font-semibold">{agent.active_contacts}</span>
                    <span className="text-xs text-text-secondary">/ {agent.max_leads}</span>
                </div>
            </td>

            {/* Velocidad respuesta */}
            <td className="py-4 px-3">
                <span className="text-sm font-semibold">
                    {agent.avg_response_hours > 0 ? `${agent.avg_response_hours}h` : '—'}
                </span>
            </td>

            {/* Conversion Rate */}
            <td className="py-4 px-3">
                <div className="flex items-center gap-2">
                    <span className={`text-sm font-bold ${agent.conversion_rate >= 30 ? 'text-emerald-500' : agent.conversion_rate >= 15 ? 'text-amber-500' : 'text-slate-500'}`}>
                        {agent.conversion_rate}%
                    </span>
                </div>
            </td>

            {/* Descuidados */}
            <td className="py-4 px-3">
                {agent.neglected_contacts > 0
                    ? <span className="inline-flex items-center gap-1 text-red-500 font-semibold text-sm">
                        <span className="material-symbols-outlined text-[16px]">warning</span>
                        {agent.neglected_contacts}
                    </span>
                    : <span className="text-emerald-500 font-semibold text-sm">✓ 0</span>
                }
            </td>

            {/* Total */}
            <td className="py-4 pl-3 pr-6 text-sm text-text-secondary text-right">
                {agent.total_contacts}
            </td>
        </tr>
    );
}

export default function ManagerDashboard() {
    const [activeTab, setActiveTab] = useState('overview'); // 'overview' | 'rules'

    const { data, isLoading, error } = useQuery({
        queryKey: ['manager-dashboard'],
        queryFn: getManagerDashboard,
        refetchInterval: 60000,
    });

    if (isLoading) {
        return (
            <div className="flex-1 flex items-center justify-center">
                <div className="w-8 h-8 rounded-full border-4 border-primary/30 border-t-primary animate-spin" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex-1 flex items-center justify-center">
                <p className="text-red-500">Error al cargar el dashboard. Verifica tu rol.</p>
            </div>
        );
    }

    const summary = data?.summary ?? {};
    const agentsRaw = data?.agents;
    const agents = Array.isArray(agentsRaw) ? agentsRaw : (agentsRaw?.data || []);

    return (
        <div className="flex-1 overflow-auto">
            {/* Page Header */}
            <div className="sticky top-0 z-10 bg-white dark:bg-background-dark border-b border-gray-100 dark:border-gray-800 px-8 py-5">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold">Panel del Manager</h1>
                        <p className="text-sm text-text-secondary mt-0.5">Rendimiento y carga de tu equipo de ventas</p>
                    </div>
                    <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
                        <button
                            onClick={() => setActiveTab('overview')}
                            className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-all ${activeTab === 'overview' ? 'bg-white dark:bg-gray-700 shadow text-primary' : 'text-text-secondary'}`}
                        >
                            Resumen
                        </button>
                        <button
                            onClick={() => setActiveTab('rules')}
                            className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-all ${activeTab === 'rules' ? 'bg-white dark:bg-gray-700 shadow text-primary' : 'text-text-secondary'}`}
                        >
                            Reglas de Asignación
                        </button>
                    </div>
                </div>
            </div>

            <div className="px-8 py-6 space-y-6">
                {activeTab === 'overview' && (
                    <>
                        {/* KPI Cards */}
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                            <KpiCard icon="group" label="Total Contactos" value={summary.total_contacts ?? 0} />
                            <KpiCard icon="bolt" label="Leads Activos" value={summary.active_contacts ?? 0} sub="En proceso" color="text-blue-500" />
                            <KpiCard icon="trending_up" label="Conversión Global" value={`${summary.conversion_rate ?? 0}%`} sub="De leads a cierre" color="text-emerald-500" />
                            <KpiCard
                                icon="warning"
                                label="Sin Contacto (≥3d)"
                                value={summary.neglected_contacts ?? 0}
                                sub="Requieren atención"
                                color="text-red-500"
                            />
                        </div>

                        {/* Agents Table */}
                        <div className="bg-white dark:bg-surface-dark rounded-xl border border-gray-100 dark:border-gray-800 overflow-hidden">
                            <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800">
                                <h2 className="text-base font-bold">Rendimiento por Vendedor</h2>
                                <p className="text-xs text-text-secondary mt-0.5">Actualizado en tiempo real</p>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="bg-gray-50 dark:bg-gray-800/60 text-xs font-bold uppercase tracking-wider text-text-secondary">
                                            <th className="py-3 pl-6 pr-3 text-left">Vendedor</th>
                                            <th className="py-3 px-3 text-left">Carga de Cartera</th>
                                            <th className="py-3 px-3 text-left">Velocidad Resp.</th>
                                            <th className="py-3 px-3 text-left">Conversión</th>
                                            <th className="py-3 px-3 text-left">Descuidados</th>
                                            <th className="py-3 pl-3 pr-6 text-right">Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {agents.length === 0 ? (
                                            <tr>
                                                <td colSpan={6} className="py-12 text-center text-text-secondary">
                                                    No hay vendedores registrados.
                                                </td>
                                            </tr>
                                        ) : (
                                            agents.map(agent => <AgentRow key={agent.id} agent={agent} />)
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}

                {activeTab === 'rules' && <AssignmentRulesPanel />}
            </div>
        </div>
    );
}
